import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ImageUpload from '../components/ImageUpload';
import { createListing, updateListing, getListing, getCategories } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const NewListingPage = () => {
    const { id } = useParams();
    const isEditing = !!id;
    const { advertiser, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingListing, setFetchingListing] = useState(isEditing);
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: '',
        subcategory: '',
        price: '',
        location: '',
        contact_email: '',
        contact_phone: '',
        images: []
    });

    useEffect(() => {
        // Only redirect after auth has finished loading and user is definitely not authenticated
        if (!authLoading && !advertiser && !localStorage.getItem('hmc_token')) {
            navigate('/advertiser/login');
        }
    }, [advertiser, authLoading, navigate]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await getCategories();
                setCategories(response.data);
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        if (isEditing && id) {
            const fetchListing = async () => {
                try {
                    const response = await getListing(id);
                    const listing = response.data;
                    setFormData({
                        title: listing.title,
                        description: listing.description,
                        category: listing.category,
                        subcategory: listing.subcategory || '',
                        price: listing.price ? String(listing.price) : '',
                        location: listing.location,
                        contact_email: listing.contact_email || '',
                        contact_phone: listing.contact_phone || '',
                        images: listing.images || []
                    });
                } catch (error) {
                    toast.error('Failed to load listing');
                    navigate('/dashboard');
                } finally {
                    setFetchingListing(false);
                }
            };
            fetchListing();
        }
    }, [id, isEditing, navigate]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleCategoryChange = (value) => {
        setFormData({ ...formData, category: value, subcategory: '' });
    };

    const handleSubcategoryChange = (value) => {
        setFormData({ ...formData, subcategory: value === 'none' ? '' : value });
    };

    const handleImagesChange = (newImages) => {
        setFormData({ ...formData, images: newImages });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title || !formData.description || !formData.category || !formData.location) {
            toast.error('Please fill in all required fields');
            return;
        }

        setLoading(true);

        try {
            const submitData = {
                ...formData,
                price: formData.price ? parseFloat(formData.price) : null,
                subcategory: formData.subcategory || null
            };

            if (isEditing) {
                await updateListing(id, submitData);
                toast.success('Listing updated successfully!');
            } else {
                await createListing(submitData);
                toast.success('Listing created successfully!');
            }
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save listing');
        } finally {
            setLoading(false);
        }
    };

    const currentCategory = categories.find(c => c.slug === formData.category);

    if (authLoading || fetchingListing) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="spinner" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="bg-gray-50 min-h-screen py-8" data-testid="new-listing-page">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Back Link */}
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="inline-flex items-center gap-2 text-gray-600 hover:text-navy mb-6"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Dashboard
                    </button>

                    <div className="bg-white rounded-xl shadow-sm p-6 sm:p-8">
                        <h1 className="font-heading text-2xl font-bold text-navy mb-6">
                            {isEditing ? 'Edit Listing' : 'Create New Listing'}
                        </h1>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Images Section */}
                            <div className="pb-6 border-b">
                                <ImageUpload
                                    images={formData.images}
                                    onChange={handleImagesChange}
                                    maxImages={5}
                                />
                            </div>

                            {/* Basic Info */}
                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="title">Title *</Label>
                                    <Input
                                        id="title"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        placeholder="e.g., Hotel Lobby Furniture Set"
                                        required
                                        className="mt-1"
                                        data-testid="title-input"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="description">Description *</Label>
                                    <Textarea
                                        id="description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        placeholder="Describe your listing in detail..."
                                        required
                                        rows={5}
                                        className="mt-1"
                                        data-testid="description-input"
                                    />
                                </div>
                            </div>

                            {/* Category */}
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <Label>Category *</Label>
                                    <Select value={formData.category} onValueChange={handleCategoryChange}>
                                        <SelectTrigger className="mt-1" data-testid="category-select">
                                            <SelectValue placeholder="Select category" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {categories.map((cat) => (
                                                <SelectItem key={cat.slug} value={cat.slug}>
                                                    {cat.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {currentCategory?.subcategories?.length > 0 && (
                                    <div>
                                        <Label>Subcategory</Label>
                                        <Select value={formData.subcategory || 'none'} onValueChange={handleSubcategoryChange}>
                                            <SelectTrigger className="mt-1" data-testid="subcategory-select">
                                                <SelectValue placeholder="Select subcategory" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">None</SelectItem>
                                                {currentCategory.subcategories.map((sub) => (
                                                    <SelectItem key={sub.slug} value={sub.slug}>
                                                        {sub.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>

                            {/* Price & Location */}
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="price">Price (optional)</Label>
                                    <div className="relative mt-1">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                        <Input
                                            id="price"
                                            name="price"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={formData.price}
                                            onChange={handleChange}
                                            placeholder="0.00"
                                            className="pl-8"
                                            data-testid="price-input"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="location">Location *</Label>
                                    <Input
                                        id="location"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleChange}
                                        placeholder="e.g., Miami, FL"
                                        required
                                        className="mt-1"
                                        data-testid="location-input"
                                    />
                                </div>
                            </div>

                            {/* Contact Info */}
                            <div>
                                <h3 className="font-semibold text-navy mb-4 pb-2 border-b">Contact Information</h3>
                                <p className="text-sm text-gray-500 mb-4">
                                    Leave blank to use your account email and phone
                                </p>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="contact_email">Contact Email</Label>
                                        <Input
                                            id="contact_email"
                                            name="contact_email"
                                            type="email"
                                            value={formData.contact_email}
                                            onChange={handleChange}
                                            placeholder={advertiser?.email}
                                            className="mt-1"
                                            data-testid="contact-email-input"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="contact_phone">Contact Phone</Label>
                                        <Input
                                            id="contact_phone"
                                            name="contact_phone"
                                            type="tel"
                                            value={formData.contact_phone}
                                            onChange={handleChange}
                                            placeholder={advertiser?.phone}
                                            className="mt-1"
                                            data-testid="contact-phone-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Submit */}
                            <div className="flex gap-4 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => navigate('/dashboard')}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    className="flex-1 bg-gold hover:bg-amber-600"
                                    disabled={loading}
                                    data-testid="submit-btn"
                                >
                                    {loading ? 'Saving...' : isEditing ? 'Update Listing' : 'Create Listing'}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default NewListingPage;
